import React, { useCallback, useEffect, useState } from 'react';
import { createNewCatchRound, GameShell, usePausableTimeout } from '../shared';
import brainGameLevels from '../../../lib/brainGameLevels';
import { formatGameCopy } from './gameCopy';

const { normalizeBrainGameLevel } = brainGameLevels;

function NewCatchGame({ copy = {}, initialLevel, isPaused = false, ...props }) {
    const startingLevel = normalizeBrainGameLevel('new-catch', initialLevel);
    const { clear: clearTimer, schedule: scheduleTimer } = usePausableTimeout(isPaused);
    const [trialIndex, setTrialIndex] = useState(1);
    const [level, setLevel] = useState(startingLevel);
    const [trial, setTrial] = useState(() => createNewCatchRound(startingLevel));
    const [phase, setPhase] = useState('preview');
    const [choice, setChoice] = useState(null);
    const [score, setScore] = useState(0);
    const [streak, setStreak] = useState(0);
    const correctChoice = choice === trial.targetIndex;

    const beginRound = useCallback((nextTrialIndex, nextLevel, excludeSetId = '') => {
        const nextTrial = createNewCatchRound(nextLevel, excludeSetId);
        setTrialIndex(nextTrialIndex);
        setLevel(nextLevel);
        setTrial(nextTrial);
        setChoice(null);
        setPhase('preview');
        scheduleTimer(
            () => setPhase('answer'),
            Math.max(1050, 1550 - (nextLevel - 1) * 35)
        );
    }, [scheduleTimer]);

    useEffect(() => {
        scheduleTimer(
            () => setPhase('answer'),
            Math.max(1050, 1550 - (startingLevel - 1) * 35)
        );
        return clearTimer;
    }, [clearTimer, scheduleTimer, startingLevel]);

    const choose = (index) => {
        if (isPaused || phase !== 'answer' || choice !== null) {
            return;
        }

        const correct = index === trial.targetIndex;
        const nextStreak = correct ? streak + 1 : 0;
        const nextLevel = normalizeBrainGameLevel(
            'new-catch',
            level + (correct && nextStreak % 2 === 0 ? 1 : 0)
        );
        setChoice(index);
        setScore((current) => current + (correct ? 1 : 0));
        setStreak(nextStreak);
        setPhase('feedback');
        scheduleTimer(
            () => beginRound(trialIndex + 1, nextLevel, trial.assetSet.id),
            correct ? 700 : 950
        );
    };

    return (
        <GameShell
            {...props}
            copy={copy}
            disabled={isPaused}
            metrics={{ level, score, streak }}
            tone={correctChoice ? 'good' : choice !== null ? 'miss' : 'ready'}
        >
            <div className={`UFOV-brain-games__catch-game is-${phase}`}>
                <div className="UFOV-brain-games__game-cue">{copy.newCatchCue}</div>
                <div className={`UFOV-brain-games__catch-grid is-${trial.cells.length}`} aria-label={copy.newCatchCue}>
                    {trial.cells.map((cell, index) => {
                        const displayedItem = phase !== 'preview' && index === trial.targetIndex ? trial.replacement : cell.item;
                        const picked = choice === index;
                        const answer = choice !== null && index === trial.targetIndex;
                        return (
                            <button
                                aria-label={formatGameCopy(copy.answerOption, { number: index + 1 })}
                                className={`UFOV-brain-games__catch-tile ${picked ? 'is-picked' : ''} ${answer ? 'is-correct' : ''}`.trim()}
                                disabled={isPaused || phase !== 'answer'}
                                key={cell.id}
                                onClick={() => choose(index)}
                                type="button"
                            >
                                <span className="UFOV-brain-games__catch-tile-inner">
                                    <img alt="" draggable="false" src={displayedItem.src} />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </GameShell>
    );
}

export default NewCatchGame;
